<script setup lang="ts">
/**
 *
 * Common modals used throughout the application and triggered via the event bus.
 *
 * Currently:
 * - login/signup
 * - unload alert
 * - payment
 */
import { storeToRefs } from 'pinia';

import Markdown from '@clovyr/bed/components/blocks/Markdown.vue';
import { UserSubscription, UserSubscriptionItem } from '@clovyr/pollen';
import type { Manifest } from '@clovyr/pollen/manifest';
import { AuthState } from '@clovyr/pollen/pollen';
import type { PaymentMethod } from '@clovyr/pollen/subscription';
import { deepCopy } from '@clovyr/pollen/util/deep-copy';
import { sleep } from '@clovyr/pollen/util/sleep';

import LaunchPaymentModal from '@/components/blocks/LaunchPaymentModal.vue';
import PaymentModal from '@/components/blocks/PaymentModal.vue';
import Modal, { type ModalSize } from '@/components/elements/Modal.vue';
import Overlay from '@/components/elements/Overlay.vue';
import { useAppResume } from '@/composables/useAppResume';
import { useAppStatus } from '@/composables/useAppStatus';
import Toast from '@/global/Toast.vue';
import {
  type ConfirmModalEvent,
  type ShowLaunchPaymentModalEvent,
  type ShowLoginEvent,
  type ShowPaymentModalEvent,
  useEventBus,
} from '@/stores/event_bus';
import { usePollenStore } from '@/stores/pollen_store';
import AuthForm from '@/views/Settings/AuthForm.vue';
import { ShowAuthForm } from '@/views/Settings/types';

const { eventBus } = useEventBus();
const { authState, isUserAuthenticated } = storeToRefs(usePollenStore());
const router = useRouter();

// Confirmation modal state

const showConfirm = ref(false);
const confirmText = ref('');
const confirmTitle = ref('Confirm');
const confirmOkLabel = ref('OK');
const confirmCancelLabel = ref('Cancel');
let confirmResolve: ((result: boolean) => void) | null = null;

function handleShowConfirmModal(payload: ConfirmModalEvent = {}) {
  confirmTitle.value = payload.title || 'Confirm';
  confirmText.value = payload.text || '';
  confirmOkLabel.value = payload.okLabel || 'OK';
  confirmCancelLabel.value = payload.cancelLabel || 'Cancel';
  showConfirm.value = true;
  confirmResolve = payload.resolve ?? null;
}

function onConfirmOk() {
  showConfirm.value = false;
  if (confirmResolve) {
    confirmResolve(true);
  }
  confirmResolve = null;
}

function onConfirmCancel() {
  showConfirm.value = false;
  if (confirmResolve) {
    confirmResolve(false);
  }
  confirmResolve = null;
}

// auth
const showAuthDialog = ref(false);
const loginData = ref<ShowLoginEvent>();
const authTheme: Ref<ModalSize> = ref('narrow');

// unload
const showUnloadAlert = ref(false);

// payments
const showPaymentModal = ref(false);
const paymentID = ref<string>('');
const paymentName = ref<string>('');
const sub = ref<UserSubscription>();
const subItem = ref<UserSubscriptionItem>();
const showLaunchPaymentModal = ref(false);
const launchPaymentApp = ref<Manifest>();

// launching overlay
const showLaunching = ref(false);
const launchingStatus = ref('Launching');
const launchDots = ref(true);

// toast
const showToast = ref(false);
let toastTimeoutId: ReturnType<typeof setTimeout> | null = null;
const toastMessage = ref('');
const toastType: Ref<'success' | 'info' | 'error'> = ref('info');
const toastData = ref({}); // placeholder for future data

const handleAuthModalClose = () => {
  showAuthDialog.value = false;
  if (
    // @ts-expect-error prop does not exist on some types
    loginData.value?.redirOnCancel ||
    loginData.value?.form === ShowAuthForm.ResendVerification ||
    loginData.value?.form === ShowAuthForm.VerificationComplete
  ) {
    void router.push('/');
  }
  eventBus.emit('modal:login:close');
};

const handleThemeChange = (theme) => {
  authTheme.value = theme;
};

const handleShowLoginModal = (data: ShowLoginEvent | undefined) => {
  const loginOrSignup =
    !data?.form || data?.form === ShowAuthForm.Login || data?.form === ShowAuthForm.Signup;
  if (authState.value === AuthState.LoggedIn && loginOrSignup) {
    // already logged-in, don't show a form now
    showAuthDialog.value = false;
    if (window.location.pathname.match(/(login|signup)$/)) {
      void router.push('/');
    }
    return;
  }

  const d = data ? deepCopy(data) : {};
  let autoForm: ShowAuthForm | undefined;
  if (!d.form || authState.value !== AuthState.LoggedOut) {
    if (authState.value === AuthState.Locked) {
      autoForm = ShowAuthForm.Locked;
    } else if (authState.value === AuthState.LoggedInWithoutSecretKey) {
      autoForm = ShowAuthForm.SecretKeyForm;
    } else {
      autoForm = ShowAuthForm.Login;
    }
  }
  if (loginOrSignup && autoForm && autoForm !== d?.form) {
    // override the requested form
    d.form = autoForm;
  }

  loginData.value = d;
  showAuthDialog.value = true;
};

const handleShowUnloadModal = () => {
  showUnloadAlert.value = true;
};

const continueToPayment = () => {
  eventBus.off('modal:login:close', continueToPayment);
  if (isUserAuthenticated.value && authState.value === AuthState.LoggedIn) {
    showPaymentModal.value = true;
  }
};

const handleShowPaymentModal = async (e: ShowPaymentModalEvent) => {
  paymentID.value = e.id;
  paymentName.value = e.name;
  sub.value = e.sub;
  subItem.value = e.subItem;

  if (isUserAuthenticated.value && authState.value === AuthState.LoggedIn) {
    // if payment previously captured, do upgrade right away
    if (sub.value.payment_captured_at) {
      const { doUpgrade } = useAppStatus(paymentID);
      if (
        await doUpgrade(
          paymentID.value,
          sub.value,
          subItem.value,
          sub.value.subscription_provider as PaymentMethod,
        )
      ) {
        void useAppResume(paymentID).wakeUpInstance();
      }
      return;
    }
    // show if signed up & logged in
    showPaymentModal.value = true;
  } else {
    // show signup form
    loginData.value = { form: ShowAuthForm.Signup };
    showAuthDialog.value = true;
    eventBus.on('modal:login:close', continueToPayment);
  }
};

const handleShowLaunchPaymentModal = async (e: ShowLaunchPaymentModalEvent) => {
  showLaunchPaymentModal.value = true;
  launchPaymentApp.value = e.app;
};

const handleLaunchPaymentModalClose = async () => {
  showLaunchPaymentModal.value = false;
  launchPaymentApp.value = undefined;
  eventBus.emit('modal:launch_payment:close');
};

const handleShowToast = ({ message, type, duration = 3000, sticky = false, data = {} }) => {
  toastMessage.value = message || '';
  toastType.value = type;
  toastData.value = data || {};
  if (toastTimeoutId) {
    clearTimeout(toastTimeoutId);
  }
  showToast.value = true;
  if (sticky) {
    return;
  }
  toastTimeoutId = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId = null;
  }, duration);
};

const handleShowLaunching = () => {
  launchingStatus.value = 'Launching';
  launchDots.value = true;
  showLaunching.value = true;
};
const handleHideLaunching = () => {
  showLaunching.value = false;
};
const handleLaunchingStatus = (status: string) => {
  launchingStatus.value = status;
  if (status === 'Done') {
    launchDots.value = false;
    void sleep(1000).then(() => {
      showLaunching.value = false;
    });
  }
};

onMounted(() => {
  eventBus.on('modal:login:show', handleShowLoginModal);
  eventBus.on('modal:login:force_close', () => {
    showAuthDialog.value = false;
  });
  eventBus.on('modal:unload:show', handleShowUnloadModal);
  eventBus.on('modal:payment:show', handleShowPaymentModal);
  eventBus.on('modal:launch_payment:show', handleShowLaunchPaymentModal);
  eventBus.on('toast:show', handleShowToast);
  eventBus.on('overlay:launching:show', handleShowLaunching);
  eventBus.on('overlay:launching:hide', handleHideLaunching);
  eventBus.on('overlay:launching:status', handleLaunchingStatus);
  eventBus.on('modal:confirm:show', handleShowConfirmModal);
});

onUnmounted(() => {
  eventBus.off('modal:login:show', handleShowLoginModal);
  eventBus.off('modal:unload:show', handleShowUnloadModal);
  eventBus.off('modal:payment:show', handleShowPaymentModal);
  eventBus.off('modal:launch_payment:show', handleShowLaunchPaymentModal);
  eventBus.off('modal:login:close', continueToPayment);
  eventBus.off('toast:show', handleShowToast);
  eventBus.off('overlay:launching:show', handleShowLaunching);
  eventBus.off('overlay:launching:hide', handleHideLaunching);
  eventBus.off('overlay:launching:status', handleLaunchingStatus);
  eventBus.off('modal:confirm:show', handleShowConfirmModal);
});
</script>

<template>
  <!-- Login/signup form -->
  <AuthForm
    v-if="showAuthDialog"
    :active-option="loginData?.form"
    :loginData="loginData"
    @close="handleAuthModalClose"
    @update-theme="handleThemeChange"
  />

  <!-- Unload alert -->
  <Modal
    v-if="showUnloadAlert"
    heading-text="Warning: you currently have one or more applications launching."
    :size="authTheme"
    :has-icon="true"
    @close="showUnloadAlert = false"
  >
    <template #body>
      <p>
        Refreshing or navigating away will interrupt the process and the application may fail to
        launch correctly.
      </p></template
    >
  </Modal>

  <LaunchPaymentModal
    v-if="showLaunchPaymentModal && launchPaymentApp"
    :app="launchPaymentApp"
    @close="handleLaunchPaymentModalClose"
  />

  <PaymentModal
    v-if="showPaymentModal"
    :id="paymentID"
    :name="paymentName"
    :subscription="sub"
    :subscriptionItem="subItem"
    @close="showPaymentModal = false"
  />

  <Teleport v-if="showToast" to="body">
    <Toast :show="showToast" :message="toastMessage" :type="toastType" @hide="showToast = false" />
  </Teleport>

  <Overlay v-if="showLaunching" :show="showLaunching" center blur>
    <ImageMedia filename="otter_yawning.webp" />
    <h2 :class="{ launchDots }">{{ launchingStatus }}</h2>
  </Overlay>

  <!-- Global Confirmation Modal -->
  <Modal
    v-if="showConfirm"
    :heading-text="confirmTitle"
    :size="authTheme"
    :has-icon="true"
    @close="onConfirmCancel"
  >
    <template #body>
      <p class="confirm-body-max"><Markdown :content="confirmText" /></p>
    </template>
    <template #controls-left>
      <Button @click="onConfirmCancel">{{ confirmCancelLabel }}</Button>
    </template>
    <template #controls-right>
      <Button reversed @click="onConfirmOk">{{ confirmOkLabel }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.launchDots:after {
  content: '.';
  animation: loading 1s ease alternate infinite;
}

@keyframes loading {
  60% {
    text-shadow: 0.35em 0 0 currentColor;
  }
  100% {
    text-shadow:
      0.35em 0 0 currentColor,
      0.75em 0 0 currentColor;
  }
}

@keyframes ellipsis {
  to {
    width: 1.25em;
  }
}

.confirm-body-max {
  max-width: 400px;
}
</style>
